import React from "react";

const formatNumber = (value, decimals = 0) => {
  if (isNaN(value) || !isFinite(value)) {
    return decimals > 0 ? (0).toFixed(decimals) : "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatCurrency = (value) => {
  return "$" + formatNumber(value);
};

export function InvestorPaymentTimeline({ results, rentalPeriod }) {
  return (
    <div className="bg-blue-50 p-3 sm:p-4 md:p-5 rounded-lg">
      <h4 className="text-sm sm:text-base md:text-lg font-medium text-gray-800 mb-3 sm:mb-4 text-center">
        Investor Payment Timeline
      </h4>

      {/* SVG Timeline Chart */}
      <div className="w-full overflow-x-auto">
        <div className="min-w-[300px] w-full">
          <svg
            width="100%"
            height="180"
            viewBox="0 0 800 180"
            preserveAspectRatio="xMidYMid meet"
            className="bg-white rounded border"
          >
            {/* Grid lines */}
            {Array.from({ length: 5 }, (_, i) => {
              const y = 30 + i * 30;
              return (
                <line
                  key={`payment-grid-${i}`}
                  x1="50"
                  y1={y}
                  x2="750"
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              );
            })}

            {/* Y-axis labels */}
            {Array.from({ length: 5 }, (_, i) => {
              const maxPayment =
                (results.constructionLoan || 300000) +
                (results.upfrontRental || 0);
              const value = maxPayment - i * (maxPayment / 4);
              const y = 30 + i * 30;
              return (
                <text
                  key={`payment-y-label-${i}`}
                  x="40"
                  y={y + 5}
                  textAnchor="end"
                  fontSize="10"
                  fill="#6b7280"
                >
                  ${formatNumber(value / 1000, 0)}k
                </text>
              );
            })}

            {/* X-axis labels */}
            {Array.from({ length: 5 }, (_, i) => {
              const month = Math.round((i * rentalPeriod) / 4);
              const x = 50 + i * 175;
              return (
                <text
                  key={`payment-x-label-${i}`}
                  x={x}
                  y="170"
                  textAnchor="middle"
                  fontSize="10"
                  fill="#6b7280"
                >
                  {month}m
                </text>
              );
            })}

            {/* Cumulative payment line */}
            <polyline
              points={(results.investorPaymentData || [])
                .map((d, i) => {
                  const x = 50 + (i * 700) / rentalPeriod;
                  const maxValue =
                    (results.constructionLoan || 300000) +
                    (results.upfrontRental || 0);
                  const y = 150 - (d.cumulative / maxValue) * 120;
                  return `${x},${y}`;
                })
                .join(" ")}
              fill="none"
              stroke="#f59e0b"
              strokeWidth="3"
            />

            {/* Payment markers */}
            {(results.investorPaymentData || [])
              .filter((d) => d.hasPayment)
              .map((d, i) => {
                const x = 50 + (d.month * 700) / rentalPeriod;
                const maxValue =
                  (results.constructionLoan || 300000) +
                  (results.upfrontRental || 0);
                const y = 150 - (d.cumulative / maxValue) * 120;
                return (
                  <g key={`payment-marker-${i}`}>
                    <circle cx={x} cy={y} r="5" fill="#dc2626" />
                    <text
                      x={x}
                      y={y - 10}
                      textAnchor="middle"
                      fontSize="8"
                      fill="#dc2626"
                      fontWeight="bold"
                    >
                      ${formatNumber(d.paymentAmount / 1000, 0)}k
                    </text>
                  </g>
                );
              })}

            {/* Axis lines */}
            <line
              x1="50"
              y1="30"
              x2="50"
              y2="150"
              stroke="#374151"
              strokeWidth="2"
            />
            <line
              x1="50"
              y1="150"
              x2="750"
              y2="150"
              stroke="#374151"
              strokeWidth="2"
            />

            {/* Chart title */}
            <text
              x="400"
              y="20"
              textAnchor="middle"
              fontSize="12"
              fill="#374151"
              fontWeight="bold"
            >
              Total Investor Payments Over {rentalPeriod} Months
            </text>
          </svg>
        </div>
      </div>

      {/* Payment Schedule Legend */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-0.5 bg-amber-500 mr-2"></div>
            <span className="text-xs text-gray-600">Cumulative Investment</span>
          </div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 bg-red-600 rounded-full mr-2"></div>
            <span className="text-xs text-gray-600">Payment Events</span>
          </div>
        </div>
        <div className="text-center">
          <div className="bg-white p-2 rounded border">
            <span className="text-xs text-gray-600">Construction Loan:</span>
            <div className="font-bold text-gray-800 text-xs sm:text-sm">
              {formatCurrency(results.constructionLoan)}
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="bg-white p-2 rounded border">
            <span className="text-xs text-gray-600">Total Payment:</span>
            <div className="font-bold text-blue-600 text-xs sm:text-sm">
              {formatCurrency(results.constructionLoan + results.upfrontRental)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Rental + Construction)
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Payment Schedule */}
      <div className="mt-4 bg-white p-3 sm:p-4 rounded-lg border">
        <h5 className="text-xs sm:text-sm font-medium text-gray-700 mb-3">
          Detailed Payment Schedule:
        </h5>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(results.investorPaymentData || [])
            .filter((d) => d.hasPayment)
            .map((payment, i) => (
              <div key={i} className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">
                    Month {payment.month}
                    {i === 0 ? " (Project Start)" : ""}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-blue-600">
                    {formatCurrency(payment.paymentAmount)}
                  </span>
                </div>
                {i === 0 && (
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>• Upfront Rental Payment:</span>
                      <span>{formatCurrency(payment.breakdown.rental)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>
                        • Construction Loan ({results.upfrontPaymentPct}%):
                      </span>
                      <span>
                        {formatCurrency(payment.breakdown.construction)}
                      </span>
                    </div>
                  </div>
                )}
                {i > 0 && (
                  <div className="text-xs text-gray-600">
                    <span>Construction Loan Installment</span>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
